import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { Card, Justify, PagingQuery, SelectOption } from 'tea-component';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import useMetricsTableModel from '@/pages/intelligence/pages/TopCharts/PCGames/hooks/useMetricsTableModel';
import {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import TableContent from '@/pages/intelligence/pages/TopCharts/PCGames/components/Tables/MetricsTableTable';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { PCConsolePageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/type';
import {
  getMetricsTableDataRankList,
  getMetricsTableDataTrends,
} from '@/pages/intelligence/services/topcharts';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import ChartIconType from '@/pages/intelligence/components/ChartsComponents/ChartIconType';
import MetricsTableVisualis from '@/pages/intelligence/pages/TopCharts/PCGames/components/Visualis/MetricsTableVisualis';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes, dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { dbiTopChartsBtnLogMes } from '@/pages/intelligence/utils/topChartsBuriedPointCfg';
import useSegmentAnchor from '@/pages/intelligence/pages/TopCharts/PCGames/components/SliderSegement/useSegmentAnchor';
import {
  getDataSourcesMetricsByTopChartsType,
  getURLSearchParams,
  mapOptionValue2Text,
  updateURLSearchParams,
} from '@/pages/intelligence/utils/commonUtils';
import { SteamSalesList } from '@/pages/intelligence/utils/echartsConst';
import { getFilterName, getFilterValueStorage } from '@/pages/intelligence/components/Filter/util';
import { useSelector, getLocale, history, useDispatch } from 'umi';
import { GuideCompetitorDelay as GuideCompetitor } from '@intelligence/components/UserGuide/Competitor';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import {
  TopchartPageId,
  MSCIENCE_METRICS_REGIONS,
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
  PC_ENTITY_TYPE,
  CONSOLE_ENTITY_TYPE,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import CascaderMetricSelect from '@intelligence/components/CascaderMetricSelect';
import {
  getPCConsoleMetricValues,
  getDeviceOptionByType,
  getCombineTopchartDeviceParams,
} from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import { getCascaderMetricsOptionsAndValue, mergeFilterConfig } from './utils';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import {
  pcMetricTableSegementList,
  consleMetricTableSegementList,
  PCConsoleSegementList,
} from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import { getClientDataSourceOfCombineMetricsTable } from '@intelligence/pages/TopCharts/PCConsoleGames/utils';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import type { MetricsTableProps, OptType } from '@intelligence/types/topchart';
import { getSeparatedDevicesOfDeviceCategories } from '@intelligence/utils/topchartsUtils';
import { GSD_SOURCE_KEY } from '@intelligence/_config/sourceMetric';
import {
  updateShortLinkPath,
  URL_CHART_METRIC,
  URL_SHORT_LINK_NAME,
  URL_SORT_NAME,
  URL_CHART_TYPE,
  URL_ORDER,
  URL_RATIO_TYPE,
  URL_TABLE_SCROLL_SEG,
} from '@intelligence/utils/urlPathCfg';
import { ChangeSegementFunType } from '@intelligence/components/ChartsComponents/DataSourceMetricSelector';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import SummaryCard from '@/pages/intelligence/components/SummaryCard';
import SourceExplanation from '@/pages/intelligence/components/SummaryCard/components/SourceExplanation';
import useSummarySourceState from '@/pages/intelligence/components/SummaryCard/useSummarySourceState';
import { cloneDeep, isEqual } from 'lodash-es';
import {
  defaultMetaScore,
  defaultMetacritic,
  gameModeColumn,
  gameEngineColumn,
  steamTagsColumn,
} from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import FilterTipModal from '@intelligence/pages/TopCharts/PCGames/components/FilterTipModal';
import { getTipModalConfirmStatus } from '@intelligence/pages/TopCharts/PCGames/components/FilterTipModal/utils';
import { INTELLIGENCE_FILTER_MODAL_NO_LONGER_PROMPT } from '@intelligence/pages/TopCharts/PCGames/components/FilterTipModal/index';
import {
  getSourceMetricsByValue,
  getMetricOptionByValue,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import classnames from 'classnames';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useConfigureTexts from '@intelligence/hooks/useConfigureTexts';
import { pcConsoleTopChart } from '@intelligence/v3/constants/downloadLogPoint';
import { CONSOLE_SOURCE_LIST, CONSOLE_SOURCE_LIST_BASE, PCCONSOLE_SOURCE_LIST, SUMMARY_SOURCE_LIST } from '../components/GamesDetails/OverlapSteam/constants/metrics';

const { pcConsoleCascaderMetricsOptions, pcConsoleCascaderMetricsMap, pcConsoleDefaultMetrics } =
  PCConsoleSegementList;

// const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_PAGE_SIZE = 50;

export default function MetricsTable(props: MetricsTableProps) {
  const locale = getLocale(); // 中英切换方法

  const { formatMessage } = useIntlExtend();
  const {
    pageId = TopchartPageId.MetricsTable,
    entityType = 'pc',
    pageLogInfo = dbiPageLogMes.topChartsConsoleGamesMetricsTable,
    visualLoadingHeight = 402,
    tableCloumsList = [],
    cardMetricName,
    getTableApiFun = getMetricsTableDataRankList,
    getTrendApiFun = getMetricsTableDataTrends,
    filterCfg,
    filterLoading,
    rangeTimeComponentDefault,
    gsdDevices,
    downloadModalSize = 820,
    defaultParams = {},
    firstDownLoadBubleShow,
    setFirstDownLoadBubleShow = () => {},
    sortByFront = false,
    hasSearch = true,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    gameHightList,
    checkboxDisableTip,
    downloadLogConfig,
    downloadBtnLogConfig,
    shortLinkValidQuery = {},
    shortLinkFilterValue,
    urlCustomParams,
    hasExtend = true,
    isStudio = false,
    // 可视化标签联动表格排序
    visualLinkTable = false,
    // 表格排序联动可视化标签
    // tableLinkVisual,
    metricFilterValue,
    hasScrollTableSegment = false,
    isAllAndChecked = false,
    onConfirm,
    config,
  } = props;
  console.log(props);

  const dispatch = useDispatch();

  const isDigitalRevenue = pageId === TopchartPageId.DigitalRevenue;
  const isPCConsoleMetricsTable = pageId === TopchartPageId.PCConsoleMetricsTable;

  const { filterState, topchartsState } = useSelector((state: any) => ({
    filterState: state?.[FILTER_EXTEND_NAMESPACE] || {},
    topchartsState: state?.[DBI_TOPCHARTS] || {},
  }));
  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue;
  const handleFilterToApiParams =
    filterCfg?.handleFilterToApiParams instanceof Function
      ? filterCfg?.handleFilterToApiParams
      : () => {};

  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  const { configureTexts } = useConfigureTexts();
  const [pcLeftSegementList, setPCLeftSegementList] = useState<OptType[]>([]);
  const [consoleLeftSegementList, setConsoleLeftSegementList] = useState<OptType[]>([]);

  // 自定义宽表 metric 后，筛选器变更提示弹窗
  const [filterTipModalVisible, setFilterTipModalVisible] = useState<boolean>();

  // 在筛选器这里拿到缓存的值，并渲染
  const filterSaveName = hasExtend ? getFilterName(userInfo) : '';
  const filterSaveData = hasExtend ? getFilterValueStorage(userInfo) : {};
  const { filterExtend, setFilterExtend } = useFilterExtend({});
  const isOperate = hasExtend && filterState?.isOperate;
  const [filterValue, setFilterVale] = useState(isOperate ? filterExtend : DEFAULT_FORM_VALUE);
  // 保留每次的 filterValue ，用于对比当前点击 confirm 时是否发生变化
  const prevFilterValue = useRef();
  const prevFilter = useRef(filterValue);
  // 用于保存初始化页面，初次改变Metric筛选器时，时间参数未更新的问题
  const metricValueRangeTime = useRef<any>({});
  // pc&console 页宽表上方 metrics 选项是否变动
  const hasMetricSelectorChanged = useRef<boolean>(Boolean(getURLSearchParams('custom_metrics')));
  // 是否是 reset 后点击 confirm 查询
  const afterResetOnConfirmRef = useRef<boolean>();

  // 用户是否手动修改了国家区域
  const userManuallyChangedRegionRef = useRef(Boolean(getURLSearchParams('custom_region')));

  useEffect(() => {
    prevFilter.current = filterValue;
  }, [JSON.stringify(filterValue)]);

  const chartTypeCur = useRef<any>(null);
  const [deviceSortByList, setDeviceSortByList] = useState<any[]>([]);

  const defaultMetric = getDataSourcesMetricsByTopChartsType(entityType + pageId);
  const [dataSourcesColumns, setDataSourcesColumns] = useState(tableCloumsList);
  const [tableLoading, setTableLoading] = useState(true);
  const [isVisualisLoading, setIsVisualisLoading] = useState(true);
  const [metric, setMetric] = useState(
    shortLinkValidQuery[URL_CHART_METRIC] || defaultMetric?.[0].value,
  );
  const [sortByName, setSortByName] = useState(
    dataSourcesColumns.length > 0 ? dataSourcesColumns[0].text : '',
  );
  const [sortByKey, setSortByKey] = useState(
    dataSourcesColumns.length > 0 ? dataSourcesColumns[0].value : '',
  );
  // const [isError, setIsError] = useState(false);
  const [noSuitableMsg, setNoSuitableMsg] = useState(formatMessage({ id: 'common_nodata' }));
  const [metricSegVal, setMetricSegVal] = useState(SteamSalesList[0].value);
  const [scrollSegmentVal, setScrollSegmentVal] = useState(
    shortLinkValidQuery?.[URL_TABLE_SCROLL_SEG] ||
      (entityType === 'console'
        ? consleMetricTableSegementList[0]?.value
        : pcMetricTableSegementList[0]?.value),
  );
  const {
    fetchParams,
    tableTotal,
    tableData,
    chartType,
    setChartType,
    barOrPieData,
    setBarOrPieData,
    lineData,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    isNodata,
    handleClearSearch,
    transTableDataForSearch,
    handleSortByFront,
  } = useMetricsTableModel({
    entityType,
    defaultParams,
    getTableApiFun,
    getTrendApiFun,
    filterValue,
    setFilterVale,
    hasUrlShortLink: filterCfg?.hasUrlShortLink,
    sortByFront,
    setIsVisualisLoading,
    fixedSelection: gameHightList,
    metricValueRangeTime,
  });
  const { openSource, handleClickSourceTips } = useSummarySourceState();

  const { options: initialMetricSelectorOptions, value: initialMetricSelectorValues } =
    getCascaderMetricsOptionsAndValue(
      pageId,
      pcConsoleCascaderMetricsOptions,
      pcConsoleDefaultMetrics,
      fetchParams,
    );
  const newInitialMetricSelectorOptions = useMemo(
    () => initialMetricSelectorOptions.filter((ele) => ele.value !== 'storeRating'),
    [initialMetricSelectorOptions],
  );
  const [metricSelectorOptions, setMetricSelectorOptions] = useState<OptionsItemType[]>(
    newInitialMetricSelectorOptions,
  );
  const [metricSelectorValues, setMetricSelectorValues] = useState(initialMetricSelectorValues);
  const metricSelectorValuesForDownload = useMemo<[string, string][]>(() => {
    if (entityType !== PC_CONSOLE_COMBINE_ENTITY_TYPE) return [];
    if (!Array.isArray(metricSelectorValues)) return [];
    const dataSourceMetrics: [string, string][] = [];
    const dataSourcesMap: {
      [key: string]: string;
    } = {
      mscience: 'Mscience',
    };
    for (const metricSelectorValue of metricSelectorValues) {
      const { dataSource, value: metric } =
        pcConsoleCascaderMetricsMap?.[metricSelectorValue] || {};
      if (dataSource && metric) {
        const dataSourceTmp = dataSourcesMap[dataSource] || dataSource;
        dataSourceMetrics.push([dataSourceTmp, metric]);
      }
    }
    return dataSourceMetrics;
  }, [entityType, metricSelectorValues, pcConsoleCascaderMetricsMap]);

  const {
    // 所有指标的value集合
    // allValues: metricSelectorAllValues,
    // 所有可选指标的value集合
    availabledValues: metricSelectorAvailabledValues,
    // 所有禁用的指标的value集合
    // disabledValues: metricSelectorDisabledValues,
  } = useMemo<{
    allValues: string[];
    availabledValues: string[];
    disabledValues: string[];
  }>(() => {
    const allValues: string[] = [];
    const availabledValues: string[] = [];
    const disabledValues: string[] = [];
    for (const metricSelectorOption of metricSelectorOptions) {
      const metricSelectorOptionChildren = metricSelectorOption?.children;
      if (!Array.isArray(metricSelectorOptionChildren)) continue;
      for (const metricSelectorOptionChild of metricSelectorOptionChildren) {
        const metricSelectorOptionChildValue = metricSelectorOptionChild?.value;
        if (!metricSelectorOptionChildValue) continue;
        if (metricSelectorOptionChild?.disabled) {
          disabledValues.push(metricSelectorOptionChildValue);
        } else {
          availabledValues.push(metricSelectorOptionChildValue);
        }
        allValues.push(metricSelectorOptionChildValue);
      }
    }
    return {
      allValues,
      availabledValues,
      disabledValues,
    };
  }, [metricSelectorOptions]);

  const updatePCConsoleMetrics = (fetchParams: any, filterOutDisabledValue = true) => {
    // const set = new Set([...metricSelectorValues, ...pcConsoleDefaultMetrics]);
    const set = new Set([
      ...metricSelectorValues,
      // 需要过滤掉禁用项 option 的值时，是默认重置逻辑，将默认值合并处理。宽表 metric 变动后不需要更新 metrics 值
      ...(filterOutDisabledValue ? pcConsoleDefaultMetrics : []),
    ]);
    const {
      options: metricSelectorOptionsTmp,
      value: metricSelectorValuesTmp,
      disabledValue,
    } = getCascaderMetricsOptionsAndValue(
      pageId,
      pcConsoleCascaderMetricsOptions,
      Array.from(set),
      fetchParams,
      filterOutDisabledValue,
    );

    const metrics = metricSelectorValuesTmp?.map((item) => {
      const disabled = disabledValue.includes(item);
      return {
        ...pcConsoleCascaderMetricsMap?.[item],
        disabled,
      };
    });

    const metricsOpt = metricSelectorOptionsTmp.filter((t) => t.value !== 'storeRating');
    setDataSourcesColumns(metrics);
    setMetricSelectorOptions(metricsOpt);
    setMetricSelectorValues(metricSelectorValuesTmp);

    return metrics;
  };

  const updateMetricsAndSetValues = (
    newParams: any,
    isPCConsoleMetricsTable: boolean,
    filterValueHasChanged?: boolean,
    filterOutDisabledValue?: boolean,
  ) => {
    const params = newParams || {};
    if (isPCConsoleMetricsTable && filterValueHasChanged) {
      /**
       * 1. 有自定义宽表 metrics 不重置
       * 2. 未自定义宽表 metrics 沿用之前逻辑
       */
      const metrics = updatePCConsoleMetrics(params, filterOutDisabledValue);
      // 判断当前排序的指标是否存在筛选后的指标里
      const filterCurrentMetrics = metrics?.filter((t) => {
        // 因数据源metricValue和后端挂勾，没有则用dataSource
        const realDataSource = t?.metricValue || t?.dataSource;
        // return realDataSource === params?.order_source && t?.value === params?.order_metric;
        return (
          realDataSource === params?.order_source &&
          t?.value === params?.order_metric &&
          !t.disabled // 过滤掉禁用项指标，避免空指标排序
        );
      });
      if (!filterCurrentMetrics?.length) {
        const availableMetrics = metrics.filter((item) => !item.disabled);
        const metricValues = getPCConsoleMetricValues(availableMetrics);
        if (availableMetrics?.length) {
          params.order_metric = metricValues?.order_metric;
          params.order_source = metricValues?.order_source;
          params.source_metrics = metricValues?.source_metrics;
        } else {
          params.order = 'desc';
          params.order_device = 'PC';
          params.order_metric = 'meta_score';
          params.order_source = 'metacritic';
          params.source_metrics = {
            ...(entityType === 'combine' ? defaultMetacritic : {}),
          };
        }

        const firstAvailableMetric = metrics?.find((item) => !item.disabled);
        setSortByKey(firstAvailableMetric?.value);
        setSortByName(firstAvailableMetric?.text);
      }
    }
    return params;
  };

  useEffect(() => {
    const pcSegementList: OptType[] = pcMetricTableSegementList?.map((t: OptType) => ({
      ...(t || {}),
      text: typeof t?.text === 'string' ? formatMessage({ id: t?.text }) : t?.text,
    }));
    setPCLeftSegementList(pcSegementList);
    const consleSegementList: OptType[] = consleMetricTableSegementList?.map((t: OptType) => ({
      ...(t || {}),
      text: typeof t?.text === 'string' ? formatMessage({ id: t?.text }) : t?.text,
    }));
    setConsoleLeftSegementList(consleSegementList);
  }, [locale]);

  const { renderAnchorSegmentComp, updateAnchorValue, updateAnchor } = useSegmentAnchor({
    defaultValue: scrollSegmentVal,
    options: entityType === 'pc' ? pcLeftSegementList : consoleLeftSegementList,
    data: dataSourcesColumns,
    minColumnWidth: 300,
    getParentNode: () => document.querySelector('.pc-metricstable-jf .tea-justify-grid__col--left'),
    getSlideNode: () => [
      document.querySelector('#metrics-table-container .tea-table .tea-table__header'),
      document.querySelector('#metrics-table-container .tea-table .tea-table__body'),
    ],
    onChange: () => {},
    onClick: (value: string) => {
      const pointKey = `${entityType}_${value}_topChartsMetricsTableSegment`;
      const logConfig = dbiTopChartsBtnLogMes?.[pointKey];
      let extInfoText = '';

      if (entityType === 'pc') {
        const matchItem = pcLeftSegementList.find((item) => item?.value === value) as
          | OptType
          | undefined;
        extInfoText = typeof matchItem?.text === 'string' ? matchItem?.text : value;
      }
      if (entityType === 'console') {
        const matchItem = consoleLeftSegementList.find((item) => item?.value === value) as
          | OptType
          | undefined;
        extInfoText = typeof matchItem?.text === 'string' ? matchItem?.text : value;
      }
      logConfig &&
        buriedBtnPoint({
          ...logConfig,
          extInfo: `${logConfig.extInfo}-${extInfoText}`,
        });
      setScrollSegmentVal(value);
    },
  });

  useEffect(() => {
    // 表格上方指标筛选器值
    if (metricFilterValue?.length) {
      // 需要对metricFilterValue进行排序，确保顺序与筛选器options一致
      const { value: sortedMetricFilterValue } = getCascaderMetricsOptionsAndValue(
        pageId,
        pcConsoleCascaderMetricsOptions,
        metricFilterValue,
        fetchParams,
        false, // 不过滤禁用项
      );
      setMetricSelectorValues(sortedMetricFilterValue);
    }
    if (shortLinkValidQuery) {
      if (shortLinkValidQuery[URL_CHART_TYPE] !== undefined) {
        setChartType(shortLinkValidQuery?.[URL_CHART_TYPE] as ChartType);
      }
      if (shortLinkValidQuery[URL_CHART_METRIC] !== undefined) {
        dispatch({
          type: `${DBI_TOPCHARTS}/setMetricValue`,
          payload: shortLinkValidQuery[URL_CHART_METRIC],
        });
        setMetric(shortLinkValidQuery[URL_CHART_METRIC]);
        // Steamsalse用的
        // setMetricSegVal(shortLinkValidQuery[URL_CHART_METRIC]);
      }
      if (shortLinkValidQuery[URL_SORT_NAME] !== undefined) {
        setSortByKey(shortLinkValidQuery[URL_SORT_NAME]);
        setSortByName(
          tableCloumsList?.filter((t) => t?.value === shortLinkValidQuery[URL_SORT_NAME])[0]?.text,
        );
      }
      if (shortLinkValidQuery[URL_TABLE_SCROLL_SEG] !== undefined) {
        setScrollSegmentVal(shortLinkValidQuery[URL_TABLE_SCROLL_SEG]);
        updateAnchorValue(shortLinkValidQuery[URL_TABLE_SCROLL_SEG]);
      }
    }
    if (Array.isArray(tableCloumsList) && tableCloumsList?.length) {
      setDataSourcesColumns(tableCloumsList);
    }
  }, [shortLinkValidQuery, metricFilterValue, tableCloumsList]);

  useEffect(() => {
    if (!filterLoading && filterCfg?.hasUrlShortLink) {
      const query: any = {
        [URL_CHART_TYPE]: chartType,
        [URL_SORT_NAME]: fetchParams.order_metric,
        [URL_ORDER]: fetchParams.order,
        [URL_RATIO_TYPE]: fetchParams.ratio_type,
      };
      if (!!topchartsState.metricValue) {
        query[URL_CHART_METRIC] = topchartsState.metricValue;
      }
      if (hasScrollTableSegment && scrollSegmentVal) {
        query[URL_TABLE_SCROLL_SEG] = scrollSegmentVal;
      }
      updateShortLinkPath({
        shortLinkQuery: query,
      });
    }
  }, [
    filterLoading,
    chartType,
    topchartsState.metricValue,
    fetchParams.order_metric,
    fetchParams.order_source,
    fetchParams.order,
    fetchParams.ratio_type,
    scrollSegmentVal,
    hasScrollTableSegment,
  ]);

  useEffect(() => {
    setTableLoading(true);
    setIsVisualisLoading(true);
    if (!userInfo?.userid || !!filterLoading) {
      // 拿缓存值需要拿到用户id
      return;
    }

    let newFetchParams = { ...fetchParams };
    const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
    const { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
      userInfo,
      urlCustomParams,
      urlShortLinkFilter: shortLinkFilterValue,
      filterSaveData: filterSaveData?.apiValue,
      filterExtend,
      specialParams: filterCfg?.specialParams,
      isOperate,
      filterName: filterSaveName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });
    // 用于榜单metricTable的指标筛选器继承到详情页
    const metricListValueParams: string[] = [];
    const sourceMetricsValue = newFetchParams?.source_metrics || {};
    // eslint-disable-next-line no-restricted-syntax
    for (const sourceKey in sourceMetricsValue) {
      metricListValueParams.push(...sourceMetricsValue[sourceKey]);
    }
    const newFilterValuesTemp = {
      ...newFilterValues,
      // DigitalRevenue 页面地区筛选器默认 us 避免被继承影响
      ...(isDigitalRevenue
        ? {
            [filterKeyMap.REGION]: {
              regions: ['us'],
              markets: ['us'],
              type: 'region',
            },
          }
        : {}),
      metric_selection_for_trends: {
        value: {
          type: 'monthly',
          types: 'all',
          metrics: metricListValueParams,
          sources: ['all'],
          source_metrics: getSourceMetricsByValue(metricListValueParams),
          metricsOptions: getMetricOptionByValue(metricListValueParams),
          selectedMetricsValue: 'metrics',
          shotSelectValue: 'all',
          selectCutVal: 'hot',
        },
      },
    };
    // 如果有短链接，设置为继承模式
    if (isOperate || hasUrlShortLinkVal) {
      // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
      setFilterExtend(newFilterValuesTemp);
    }
    setFilterVale(newFilterValuesTemp);
    newFetchParams = {
      ...newFetchParams,
      ...(urlCustomParams || {}),
      ...handleFilterToApiParams(filterParams),
    };

    if (
      isPCConsoleMetricsTable &&
      Array.isArray(metricFilterValue) &&
      metricFilterValue?.length > 0
    ) {
      const metrics = metricFilterValue?.map((item) => pcConsoleCascaderMetricsMap?.[item]);
      const { source_metrics = {}, order_metric, order_source } = getPCConsoleMetricValues(metrics);

      // 只有 mscience 指标时默认选中美国
      const sourceMetricsKeys = Object.keys(source_metrics);
      if (
        sourceMetricsKeys?.length === 1 &&
        sourceMetricsKeys[0] === 'mscience' &&
        !userManuallyChangedRegionRef.current
      ) {
        newFetchParams.regions = ['us'];
      }

      newFetchParams.source_metrics = source_metrics || {};
      // 更新排序指标为第一个可用指标（通常是 gamalytic__pcu）
      if (order_metric && order_source) {
        newFetchParams.order_metric = order_metric;
        newFetchParams.order_source = order_source;
      }
    }

    // check 因时间粒度、国家原因更新Metrics下拉选择值，更新参数
    newFetchParams = updateMetricsAndSetValues(newFetchParams, isPCConsoleMetricsTable);
    // 如果是链接中带有当前时间粒度禁用的宽表 metrics,需要 metrics 选择器对应选项禁用态
    isPCConsoleMetricsTable && updatePCConsoleMetrics(newFetchParams, false);
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: newFilterValuesTemp,
    });
  }, [userInfo?.userid, filterLoading, shortLinkFilterValue, metricFilterValue]);

  const getData = async (args: {
    params: any;
    isCheckGame: boolean;
    isSearch?: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues?: any;
    myFilterValue?: string;
    metricFilterValue?: string[];
    tableColumns?: any[];
  }) => {
    const {
      params,
      isCheckGame,
      isSearch = false,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      myFilterValue = filterSaveName,
      metricFilterValue = metricSelectorValues,
      tableColumns = dataSourcesColumns,
    } = args;

    // 对Device做拆分拿到排序列表
    const deviceMap = {
      PC: ['PC'],
      PlayStation: ['PS4', 'PS5'],
      Xbox: ['XOne', 'XS X|S'],
      Nintendo: ['Wii U', 'NS'],
    } as any;

    const devices: string[] = params?.device || [];
    let deviceList: string[] = [];

    devices.forEach((device) => {
      if (deviceMap[device]) {
        deviceList = [...deviceList, ...deviceMap[device]];
      }
    });
    const sortByList = deviceList?.map((t) => ({
      key: `meta_score_${t}_metacritic_0`,
      order_device: t,
      sortMetric: 'meta_score',
      orderSource: 'metacritic',
      text: t,
      title: formatMessage({ id: 'dbi_metric_metacritic_global' }),
      ratioType: 0,
    }));
    setDeviceSortByList(sortByList);
    const newFormValue = {
      ...params,
      unified_ids: isSearch ? params?.unified_ids : [],
      source_metrics: {
        ...(params?.source_metrics || {}),
        ...(entityType === 'combine' ? defaultMetacritic : {}),
      },
    };
    // 只有评分才有order_device参数
    newFormValue.order_device =
      newFormValue?.order_metric === 'meta_score' ? newFormValue.order_device : '';
    try {
      await getGameData({
        params: newFormValue,
        isCheckOrSearch: isCheckGame,
        isUpdateLineChart,
        updateFilterValues,
        filterSaveName: myFilterValue || '',
        metricFilterValue,
        tableColumns,
      });
    } catch (error) {
    } finally {
      setTableLoading(false);
      setIsVisualisLoading(false);
    }
  };

  const getTrend = async (params: any, idList: string[]) => {
    try {
      setIsVisualisLoading(true);
      await getLineChartData(params, idList);
    } catch (error) {
    } finally {
      setIsVisualisLoading(false);
    }
  };

  const handleSortByFetchApi = async (newFetchParams: any, sortFun: Function) => {
    if (!operatedGame) {
      /** 没有勾选时点排序则重新Loading柱状图数据 */
      setIsVisualisLoading(true);
    }
    await sortFun?.({
      list: tableData,
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: !operatedGame,
    });
  };

  const onTableSortChage = async (e: SortByItemType) => {
    const newFetchParams = {
      ...fetchParams,
      order: e?.sortVal,
      order_device: e?.order_device ? e?.order_device : '',
      order_metric: e?.sortMetric,
      order_source: e?.dataSourceNew ?? e?.orderSource,
      ratio_type: e?.ratioType,
      page: 1,
      page_size: defaultPageSize,
    };
    setSortByName(e?.title);
    setSortByKey(e?.sortMetric);
    setTableLoading(true);
    await handleSortByFetchApi(newFetchParams, sortByFront ? handleSortByFront : getData);
    setTableLoading(false);
    if (chartType !== ChartType.Line) {
      // 折线图因为要重新请求接口，在请求接口处修改loading了
      setIsVisualisLoading(false);
    }

    if (pageId === TopchartPageId.MetricsTable) {
      if (updateAnchor instanceof Function) {
        updateAnchor();
      }
    }
    // setMetric(defaultMetric.length > 0 ? defaultMetric[0].value : '');
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const newFetchParams = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      page_size: e?.pageSize || defaultPageSize,
    };
    setTableLoading(true);
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: false,
    });
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    setIsNewComfirm(true);
    setIsNewCheckGame(true);
    const idList = selectList.map((t) => t?.gameId);
    if (chartType === ChartType.Line) {
      getTrend(fetchParams, idList);
    }
    /** 是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    setBarOrPieData(selectList);
    setTableSelection(selectList);
  };

  const getShouldShowTipModal = (filter: any) => {
    // 未自定义选中宽表 metric 不显示
    if (!hasMetricSelectorChanged.current) return false;

    // Reset 触发的时间粒度、国家变化，不显示
    if (afterResetOnConfirmRef.current) {
      afterResetOnConfirmRef.current = false;
      return false;
    }

    // 有勾选不再提示保存到 localStorage 的，不显示
    const noLongerPrompt = localStorage.getItem(INTELLIGENCE_FILTER_MODAL_NO_LONGER_PROMPT);
    if (noLongerPrompt) return false;

    // 地区、日期类型有变动才显示
    const keys = [
      ['region', 'regions'],
      ['rangeTime', 'type'],
    ];
    const shouldShowTipModal = keys.some((item) => {
      const [key, subKey] = item;
      return (
        filter?.[key] &&
        prevFilter.current?.[key] &&
        !isEqual(prevFilter.current?.[key][subKey], filter?.[key][subKey])
      );
    });

    return shouldShowTipModal;
  };

  const handleConfirm = async (value: any, filter: any, myFilterValue: any) => {
    // 时间地区修改后且有自定义宽表指标时，提示弹窗
    const shouldShowTipModal = getShouldShowTipModal(filter);
    if (shouldShowTipModal) {
      // 显示弹窗
      setFilterTipModalVisible(true);
      // 监听弹窗确认状态
      const tipModalConfirmStatus = await getTipModalConfirmStatus();

      // 撤销：将筛选器的值撤销为上次 confirm 的值
      if (!tipModalConfirmStatus) {
        setFilterVale(filterValue);
        return;
      }
    }

    // 点击 confirm 按钮时筛选项发生变化时才更新宽表 metrics
    const filterValueHasChanged = !isEqual(prevFilterValue.current, value);
    prevFilterValue.current = cloneDeep(value);
    prevFilter.current = cloneDeep(filter);
    let newFormValue = {
      ...fetchParams,
      ...value,
      page: 1,
      page_size: defaultPageSize,
    };
    // check 因时间粒度、国家原因更新Metrics下拉选择值，更新参数
    newFormValue = updateMetricsAndSetValues(
      newFormValue,
      isPCConsoleMetricsTable,
      filterValueHasChanged,
      // 宽表 metric 没有改变时沿用默认逻辑，参数传 true，发生改变时传 false，不重新添加默认值，不过滤掉禁用options的值
      !hasMetricSelectorChanged.current,
    );

    setTableLoading(true);
    setIsVisualisLoading(true);
    /** 修改筛选器后，游戏应该重置为未勾选状态 */
    setOperatedGame(false);
    // setMetric(defaultMetric.length > 0 ? defaultMetric[0].value : '');

    // 用于榜单metricTable的指标筛选器继承到详情页
    const metricListValue: string[] = [];
    const sourceMetricsValue = newFormValue?.source_metrics || {};
    for (const sourceKey in sourceMetricsValue) {
      metricListValue.push(...sourceMetricsValue[sourceKey]);
    }
    // dispatch({
    //   type: `${DBI_TOPCHARTS}/setMetricValueListObj`,
    //   payload: { metricListValue, sourceMetricsValue },
    // });

    const newFilterValuesTemp = {
      ...filter,
      metric_selection_for_trends: {
        value: {
          type: 'monthly',
          types: 'all',
          metrics: metricListValue,
          sources: ['all'],
          source_metrics: getSourceMetricsByValue(metricListValue),
          metricsOptions: getMetricOptionByValue(metricListValue),
          selectedMetricsValue: 'metrics',
          shotSelectValue: 'all',
          selectCutVal: 'hot',
        },
      },
    };
    setFilterExtend(newFilterValuesTemp);
    await getData({
      params: newFormValue,
      isCheckGame: false,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: filter,
      myFilterValue,
    });

    setIsNewComfirm(true);
    setIsNewCheckGame(true);
    if (pageId === TopchartPageId.MetricsTable) {
      handleFilterConfirmSlider(newFormValue);
    }

    onConfirm?.(value);
  };

  const handleChangeExtra = (key: string) => {
    if (key === 'region') {
      userManuallyChangedRegionRef.current = true;
      updateURLSearchParams({ custom_region: true });
    }
  };

  const handleReset = () => {
    if (isPCConsoleMetricsTable) {
      afterResetOnConfirmRef.current = true;
      // // 重置 fetchParams
      // setFetchParams(defaultParams);
      // // 更新重置宽表 metric
      // updateMetricsAndSetValues(
      //   defaultParams,
      //   isPCConsoleMetricsTable,
      //   true,
      //   true,
      //   false,
      // );
      // 重置自定义选中宽表 metric 标识
      //   hasMetricSelectorChanged.current = false;
    }
  };

  // More Filter按钮埋点
  const onToggleMoreFilter = (v: boolean) => {
    if (v) {
      const logKey = `${entityType}_${pageId}_MetricsMoreFilter`;
      const logConfig = dbiTopChartsBtnLogMes?.[logKey];
      logConfig && buriedBtnPoint(logConfig);
    }
  };
  const handleFilterConfirmSlider = (params: any) => {
    if (updateAnchorValue instanceof Function) {
      // PCU只有Global数据
      const isGlobal = params?.regions?.includes('global');
      // Digital只有固定5个国家有数据，不排除数据库的修改
      const isIncludeMYFDX = params?.regions?.some((item: string) =>
        MSCIENCE_METRICS_REGIONS.includes(item),
      );
      let availableAnchorValue;
      if (isGlobal) {
        if (entityType === 'pc') {
          availableAnchorValue = pcLeftSegementList?.[0]?.value;
          setSortByName('PCU');
        } else {
          availableAnchorValue = consoleLeftSegementList?.[0]?.value;
          setSortByName('Digital Revenue');
        }
      } else if (isIncludeMYFDX) {
        // 定位到Digitalrevenue
        setSortByName('Digital Revenue($)');
        if (entityType === 'pc') {
          availableAnchorValue = pcLeftSegementList?.[1]?.value;
        } else if (entityType === 'console') {
          availableAnchorValue = consoleLeftSegementList?.[0]?.value;
        }
      } else {
        /** 定位到MAU */
        setSortByName('MAU');
        if (entityType === 'pc') {
          availableAnchorValue = pcLeftSegementList[2]?.value;
        } else if (entityType === 'console') {
          availableAnchorValue = consoleLeftSegementList[1]?.value;
        }
      }
      if (availableAnchorValue) {
        updateAnchorValue(availableAnchorValue);
      }
    }
  };

  const getTableSearchList = (id: string) => {
    setTableLoading(true);
    // console.log('fetchParamsfetchParams',fetchParams);
    getData({
      params: { ...fetchParams, unified_ids: [id] },
      isCheckGame: true,
      isSearch: true,
      isUpdateLineChart: false,
    });
  };

  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    handleChartMetricType(value);
  };

  const handleChartMetricType = (value: ChartType) => {
    if (value === ChartType.Line && tableSelection?.length && isNewComfirm && isNewCheckGame) {
      const idList = tableSelection.map((t) => t?.gameId);
      getTrend(fetchParams, idList);
    }
    setChartType(value);
    chartTypeCur.current = value;
  };

  const changeMetricSelector = async (payload: string[]) => {
    // 将 "手动选择宽表 metric " 标识变量赋值
    hasMetricSelectorChanged.current = true;
    updateURLSearchParams({ custom_metrics: true });

    let payloadTmp = payload;
    /** 修改筛选器后，游戏应该重置为未勾选状态 */
    setOperatedGame(false);
    // 一个指标都不选时，默认勾选 前八个可选指标
    if (!payloadTmp?.length) {
      payloadTmp = metricSelectorAvailabledValues?.slice(
        0,
        Math.min(metricSelectorAvailabledValues?.length, 8),
      );
    }
    const metrics = payloadTmp?.map((item) => pcConsoleCascaderMetricsMap?.[item]);
    const logList = payloadTmp?.map((item) => {
      const id = pcConsoleCascaderMetricsMap?.[item]?.text || '';
      return formatMessage({ id });
    });
    const metricValues = getPCConsoleMetricValues(metrics);
    if (JSON.stringify(payloadTmp) === JSON.stringify(metricSelectorValues)) {
      setMetricSelectorValues(payloadTmp);
      return undefined;
    }

    const isOnlyMscienceMetric = metrics?.every(
      (metric) =>
        // ['digitalRevenue', 'units'].includes(metric?.value),
        metric.dataSource === 'mscience',
    );

    /**
     * PC&Console 榜单页将地区指定为 us 条件
     * 1. 用户只选中了指定 MScience 指标
     * 2. 当前区域为全球，用户未更改
     */
    const shouldSetUSRegion =
      isPCConsoleMetricsTable &&
      isOnlyMscienceMetric &&
      fetchParams.regions?.[0] === 'global' &&
      !userManuallyChangedRegionRef.current;

    /**
     * PC&Console 榜单页将地区重置为 global 条件
     * 1. 用户选中了除指定 MScience 指标还包括其他指标
     * 2. 当前地区为自动指定的 us，用户未更改
     */
    const shouldResetGlobalRegion =
      isPCConsoleMetricsTable &&
      !isOnlyMscienceMetric &&
      fetchParams.regions?.[0] === 'us' &&
      !userManuallyChangedRegionRef.current;

    // 表格数据重新渲染对应的列，参数重新调整为对应的数据
    const newFetchParams = {
      ...fetchParams,
      ...(shouldSetUSRegion ? { regions: ['us'] } : {}),
      ...(shouldResetGlobalRegion ? { regions: ['global'] } : {}),
      order_metric: metricValues?.order_metric,
      order_source: metricValues?.order_source,
      source_metrics: {
        ...metricValues?.source_metrics,
        ...(entityType === 'combine' ? defaultMetacritic : {}),
      },
      page: 1,
      page_size: defaultPageSize,
      date_type: metricValueRangeTime?.current?.dateType,
      start_date: metricValueRangeTime?.current?.startDate,
      end_date: metricValueRangeTime?.current?.endDate,
    };
    setMetric(metricValues?.order_metric);

    // 用于榜单metricTable的指标筛选器继承到详情页
    const metricListValue: string[] = [];
    const sourceMetricsValue = metricValues?.source_metrics || {};
    for (const sourceKey in sourceMetricsValue) {
      metricListValue.push(...sourceMetricsValue[sourceKey]);
    }
    const metricValueRangeTimeValue = {
      dateType: metricValueRangeTime?.current?.dateType,
      startDate: metricValueRangeTime?.current?.startDate,
      endDate: metricValueRangeTime?.current?.endDate,
    };
    dispatch({
      type: `${DBI_TOPCHARTS}/setMetricValueListObj`,
      payload: { metricListValue },
    });
    dispatch({
      type: `${DBI_TOPCHARTS}/setMetricValueRangeTime`,
      payload: metricValueRangeTimeValue,
    });
    const newFilterValuesTemp = {
      ...DEFAULT_FORM_VALUE,
      rangeTime: {
        ...DEFAULT_FORM_VALUE?.rangeTime,
        type: metricValueRangeTime?.current?.dateType,
        value: [metricValueRangeTime?.current?.startDate, metricValueRangeTime?.current?.endDate],
      },
      metric_selection_for_trends: {
        value: {
          type: 'monthly',
          types: 'all',
          metrics: metricListValue,
          sources: ['all'],
          source_metrics: getSourceMetricsByValue(metricListValue),
          metricsOptions: getMetricOptionByValue(metricListValue),
          selectedMetricsValue: 'metrics',
          shotSelectValue: 'all',
          selectCutVal: 'hot',
        },
      },
    };
    setFilterExtend(newFilterValuesTemp);
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperate`,
      payload: true,
    });

    dispatch({
      type: `${DBI_TOPCHARTS}/setMetricValue`,
      payload: metricValues?.order_metric,
    });

    setTimeout(() => {
      setTableLoading(true);
      setIsVisualisLoading(true);
      setMetricSelectorValues(payloadTmp);
      getData({
        params: newFetchParams,
        isCheckGame: false,
        isSearch: false,
        isUpdateLineChart: true,
        metricFilterValue: payloadTmp,
        tableColumns: metrics,
      });
      if (chartTypeCur.current === ChartType.Line && tableSelection?.length) {
        const idList = tableSelection.map((t) => t?.gameId);
        getTrend(newFetchParams, idList);
      }
      setDataSourcesColumns(metrics);
      setSortByKey(metrics?.[0]?.value);
      setSortByName(metrics?.[0]?.text);
    }, 200);
    const logConfig = dbiTopChartsBtnLogMes?.topChartsPcConsoleGamesMetricsSelector;
    logConfig &&
      buriedBtnPoint({
        ...logConfig,
        extInfo: `${logConfig?.extInfo}-${logList?.join(',')}`,
      });
  };

  useEffect(
    () => () => {
      dispatch({ type: `${DBI_TOPCHARTS}/setMetricValue`, payload: '' });
    },
    [],
  );

  const renderCascaderMetricSelect = () => (
    <CascaderMetricSelect
      locale={locale}
      options={metricSelectorOptions}
      value={metricSelectorValues}
      loading={tableLoading}
      onConfirm={changeMetricSelector}
      className="dbi-combine-metrics-table-selector"
    />
  );

  const getTableOptLeftComp = (pageId: PCConsolePageId, scrollSegmentVal?: string) => {
    if (pageId === TopchartPageId.MetricsTable) {
      return renderAnchorSegmentComp(scrollSegmentVal);
    }
    if (isPCConsoleMetricsTable) {
      return renderCascaderMetricSelect();
    }
    return null;
  };

  const optLeftComp = useMemo(
    () => getTableOptLeftComp(pageId, scrollSegmentVal),
    [
      pageId,
      locale,
      metricSelectorOptions,
      metricSelectorValues,
      pcLeftSegementList,
      consoleLeftSegementList,
      tableSelection,
      scrollSegmentVal,
      tableLoading,
    ],
  );

  const devicesOptions = useMemo<SelectOption[]>(() => {
    const options = getDeviceOptionByType(pageId);
    return Array.isArray(options) ? options : [];
  }, [pageId]);

  const devicesNames = useMemo<string>(() => {
    const devices: string[] = fetchParams?.device;
    if (!devices) return '';
    if (!Array.isArray(devices)) return '';
    return devices.map((device) => mapOptionValue2Text(device, devicesOptions)).join(', ');
  }, [devicesOptions, fetchParams?.device]);

  // 下载弹窗表单内 纯文本展示的 文本集合，下载表单配置的纯文本空间的text字段值 对应该对象的key，通过key获取展示的文本内容
  const downloadFormModelTexts = useMemo<{ [key: string]: string }>(
    () => ({
      devices: devicesNames,
    }),
    [devicesNames],
  );

  const handleDownloadParams = (params: any): any => {
    let paramsTmp: any = {
      ...params,
      pageId,
      select_ids: tableSelection,
    };
    // 让下载请求中的 order_metrics 顺序与表格列（dataSourcesColumns）顺序一致，
    if (entityType !== PC_CONSOLE_COMBINE_ENTITY_TYPE && Array.isArray(dataSourcesColumns)) {
      const normalizeDataSource = (col: any) => {
        const metric: string = col?.value || '';
        if (metric.startsWith('gamalytic__')) return 'gamalytic';
        if (metric.startsWith('source_steam__')) return 'source_steam';
        if (metric.startsWith('alinea__')) return 'alinea';
        if (metric === 'steamfollowers' || metric === 'steamreviews') return 'vginsights';
        if (metric === 'pcu' || metric === 'acu') return 'steamcharts';
        return col?.dataSource;
      };
      const orderMetricsFromColumns = dataSourcesColumns
        .filter((col: any) => col?.value)
        .map((col: any) => ({
          dataSource: normalizeDataSource(col),
          metric: col.value,
        }))
        .filter((item: any) => !!item.dataSource);
      if (orderMetricsFromColumns.length > 0) {
        paramsTmp = {
          ...paramsTmp,
          order_metrics: orderMetricsFromColumns,
        };
      }
    }
    if (entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE && isPCConsoleMetricsTable) {
      const devicesParams = params?.device;
      // GSD数据源的devices集合
      const gsdDevicesSeparated = getSeparatedDevicesOfDeviceCategories(gsdDevices, devicesParams);
      // 非GSD数据源的devices的map映射
      const otherDevicesSeparatedMap = getCombineTopchartDeviceParams(devicesParams);
      const sourceDownloadDevicesMap = {
        ...otherDevicesSeparatedMap,
        [GSD_SOURCE_KEY]: gsdDevicesSeparated,
      };
      paramsTmp = {
        ...paramsTmp,
        metrics: metricSelectorValuesForDownload,
        source_download_device: sourceDownloadDevicesMap,
        download_metrics: metricSelectorValues,
      };
    }
    return paramsTmp;
  };

  const handleChangeSegement = async (params: ChangeSegementFunType) => {
    // console.log('handleChangeSegement-visualLinkTable', visualLinkTable);
    // console.log('handleChangeSegement-params', params);
    // 可视化不用联动表格则不往下走
    if (!visualLinkTable) {
      return;
    }
    const { metric, source, tableSource, currentMetricObj } = params || {};
    const dataSource = currentMetricObj?.tableSource || tableSource || source;
    // 过滤出列表的内容
    onTableSortChage({
      sortMetric: metric,
      orderSource: dataSource,
      ratioType: 0,
      sortVal: 'desc',
    });
  };

  const handleFilterTipModalConfirm = () => {
    setFilterTipModalVisible(false);
  };

  const handleFilterTipModalCancel = () => {
    setFilterTipModalVisible(false);
  };

  const handleSteamCellTagClick = async (type: string, tag: string) => {
    // 将点击的 tag 给对应的筛选器赋值
    const currentFilterValue = { ...filterValue, [type]: { value: [tag] } };
    setFilterVale(currentFilterValue);

    // 发起请求
    const newFormValue = {
      ...fetchParams,
      [type]: [tag],
    };
    setTableLoading(true);
    setIsVisualisLoading(true);

    try {
      await getData({
        params: newFormValue,
        isCheckGame: false,
        isSearch: false,
        isUpdateLineChart: true,
        updateFilterValues: currentFilterValue,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setTableLoading(false);
      setIsVisualisLoading(false);
    }
  };

  const summarySourceConfig = useMemo(() => {
    const isConsole = entityType === CONSOLE_ENTITY_TYPE;
    return {
      summarySourceKeyList: isConsole ? CONSOLE_SOURCE_LIST : PCCONSOLE_SOURCE_LIST,
      summarySourceList: isConsole ? CONSOLE_SOURCE_LIST_BASE : SUMMARY_SOURCE_LIST,
    };
  }, [entityType]);

  return (
    <>
      <Card className="dbi-mobileGames-card-filter">
        <Card.Body>
          <Filter
            filterConfirm={handleConfirm}
            onChangeExtra={handleChangeExtra}
            filterReset={handleReset}
            onToggleMoreFilter={onToggleMoreFilter}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            defaultValue={DEFAULT_FORM_VALUE}
            filterCfg={mergeFilterConfig(filterCfg, configureTexts)}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            resetTip={
              isPCConsoleMetricsTable && formatMessage({ id: 'dbi_top_pc_console_filter_reset' })
            }
            isNewFilterExtend={true}
          />
        </Card.Body>
      </Card>
      <Card className="dbi-mobileGames-card-context dbi-pcconsole-card-title">
        <Card.Body
          title={
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>{cardMetricName}</span>
              <ArrowdownText
                isNew={true}
                sortedData={sortByName}
                order={fetchParams?.order}
                sortByKey={sortByKey}
                dataSourcesColumns={dataSourcesColumns}
              />
              <span className="dib-sourcetips-explanation-divide"></span>
              <SourceExplanation
                handleClickSourceState={handleClickSourceTips}
                openSource={openSource}
              />
            </div>
          }
          operation={
            tableLoading ? null : (
              <ChartIconType chartType={chartType} handleChartType={handleChartType} />
            )
          }
        >
          {openSource && <SummaryCard isSourceTips={true} openSource={openSource} {...summarySourceConfig} />}
          <MetricsTableVisualis
            isVisualisLoading={isVisualisLoading}
            visualLoadingHeight={visualLoadingHeight}
            orderMetric={fetchParams?.order_metric}
            fetchParams={fetchParams}
            topChartsType={entityType + pageId}
            imageType={entityType}
            entityType={entityType}
            lineData={lineData}
            barOrPieData={barOrPieData}
            chartType={chartType}
            metric={metric}
            setMetric={setMetric}
            sortByName={sortByName}
            sortByKey={sortByKey}
            defaultMetric={defaultMetric}
            dateType={fetchParams?.date_type}
            startDate={fetchParams?.start_date}
            endDate={fetchParams?.end_date}
            pageId={pageId}
            isNodata={isNodata}
            handleChartMetricType={handleChartMetricType}
            formatMessage={formatMessage}
            noSuitableMsg={noSuitableMsg}
            metricSegVal={metricSegVal}
            setMetricSegVal={setMetricSegVal}
            setNoSuitableMsg={setNoSuitableMsg}
            dataSourcesColumns={dataSourcesColumns}
            gameHightList={gameHightList}
            isStudio={isStudio}
            handleChangeSegement={handleChangeSegement}
          />
        </Card.Body>
      </Card>

      <Justify
        className="dbi-pcGame-jf pc-metricstable-jf dbi-card-justify"
        // left={getTableOptLeftComp(pageId)}
        left={optLeftComp}
        right={
          <>
            {hasSearch && (
              <SearchRecent
                searchType={`${entityType}`}
                // 传入搜索的filter_ids要为[],不然后端会返回filter_ids里的游戏列表
                fetchParams={{ ...fetchParams, page: 1, filter_ids: [] }}
                handleClear={handleClearSearch}
                getTableResultList={getTableSearchList}
                searchListApi={getTableApiFun}
                pageLogInfo={pageLogInfo}
                initSearchInput={!(fetchParams?.unified_ids?.length > 0)}
                transTableDataForSearch={transTableDataForSearch}
              />
            )}
            {/** databrain页下载不提示 */}
            <ExportTable
              downloadLogConfig={downloadLogConfig}
              downloadBtnLogConfig={downloadBtnLogConfig}
              downloadModalSize={downloadModalSize}
              isAllAndChecked={isAllAndChecked}
              entityType={entityType}
              pageId={pageId}
              businessType={`${entityType}-${pageId}`}
              isDisableDownload={tableLoading}
              total={tableTotal}
              tableParams={handleDownloadParams(fetchParams)}
              isNeedCheckedLines={true}
              defaultCheckedLines={operatedGame}
              isGameTopCharts={true}
              hasBubbleCloseIcon={firstDownLoadBubleShow}
              texts={downloadFormModelTexts}
              onBubbleClose={() => {
                setFirstDownLoadBubleShow?.(false);
                /** 下载首次提示关闭打点 ,与Activeuser页面的共用*/
                buriedBtnPoint(dbiBtnLogMes.topchartMobileGameActiveUserDownLoadTip);
              }}
              isStudio={isStudio}
              metricSelectorOptions={metricSelectorOptions}
              downloadLogConfigKey={pcConsoleTopChart?.[`${entityType}_${pageId}`]}
            />
          </>
        }
      />
      <div
        id="metrics-table-container"
        className={classnames('dbi-table', {
          'dbi-metrics-table-container-studio': isStudio,
        })}
      >
        <CardSlot className="dbi-metrics-table-container">
          <TableContent
            records={tableData}
            loading={tableLoading}
            tableTotal={tableTotal}
            tableSelection={tableSelection}
            tableSelectionAll={defaultSelectionGames}
            pageSortAttr={{
              tablePageIndex: fetchParams?.page,
              tablePageSize: fetchParams?.page_size,
              currentSortValue: fetchParams?.order,
              // 用于高亮排序
              currentSortMetric: fetchParams?.order_device
                ? `${fetchParams?.order_metric}_${fetchParams?.order_device}`
                : fetchParams?.order_metric,
              currentOrderSource: getClientDataSourceOfCombineMetricsTable(
                fetchParams?.order_source,
                fetchParams?.order_metric,
                entityType,
              ),
              currentRatioType: fetchParams?.ratio_type,
              currentOrderDevice: fetchParams?.order_device,
            }}
            onTableSortChage={onTableSortChage}
            onTablePagingChange={onTablePagingChange}
            onTableCheckGame={onTableCheckGame}
            selectDataSources={fetchParams?.data_sources}
            pageId={pageId}
            dateType={fetchParams?.date_type}
            startDate={fetchParams?.start_date}
            endDate={fetchParams?.end_date}
            genreSource={fetchParams?.genre_source ?? 'annie'}
            entityType={entityType}
            dataSourcesColumns={
              entityType === 'combine'
                ? [...dataSourcesColumns, defaultMetaScore, steamTagsColumn, gameModeColumn, gameEngineColumn]
                : dataSourcesColumns
            }
            deviceSortByList={deviceSortByList}
            gameHightList={gameHightList}
            checkboxDisableTip={checkboxDisableTip}
            disableChckekList={gameHightList}
            onTagClick={handleSteamCellTagClick}
            jumpType={config?.jumpType}
          />
        </CardSlot>
      </div>
      {/* Competitor新手引导 */}
      <GuideCompetitor />
      {/* PC&Console 宽表Metric修改后筛选项变动提示 */}
      <FilterTipModal
        visible={filterTipModalVisible}
        onCancel={handleFilterTipModalCancel}
        onConfirm={handleFilterTipModalConfirm}
      />
    </>
  );
}
